/**
 * «Браузер» Апартаментов на телефоне (PWA).
 *
 * Встроенного браузера с отдельными cookie на каждый аккаунт в PWA нет — он
 * есть только у настольного приложения. Здесь вместо него:
 *   • кнопки, открывающие площадку (её приложение или сайт) — вход по
 *     логину аккаунта компании, пароля на телефоне нет;
 *   • запись брони из текста: в приложении Airbnb или Booking открыть бронь,
 *     выделить и скопировать текст, вставить сюда — сервер разберёт его тем
 *     же разбором, что и автозапись (POST /api/stays/bookings/capture,
 *     manual), и заведёт черновик «Заполнить».
 */

import { useState } from 'react';
import { ClipboardPaste, Copy, ExternalLink, KeyRound, LifeBuoy, Luggage, MessageCircle } from 'lucide-react';
import type { StayAccountDto, StayBookingDto, StayPersonDto } from '@tw/shared-types';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSession } from '../../state/session.tsx';
import { describeError } from '../../lib/errors.ts';
import { openExternal } from '../../lib/desktop.ts';
import { BookingDialog, type BookingPrefill } from './BookingDialog.tsx';
import { EmptyState, FormField, INPUT, PlatformMark, TEXTAREA } from './bits.tsx';
import { PLATFORM, formatMoney, formatRange } from './meta.ts';

interface Props {
  accounts: StayAccountDto[];
  people: StayPersonDto[];
  accountId: string | null;
  onAccountChange: (id: string) => void;
  onBook: (prefill: BookingPrefill) => void;
}

type Outcome =
  | { kind: 'saved'; booking: StayBookingDto; existed: boolean }
  | { kind: 'incomplete' }
  | { kind: 'notFound' };

export function MobileStaysTools({ accounts, people, accountId, onAccountChange, onBook }: Props) {
  const { api, can } = useSession();
  const usable = accounts.filter((a) => a.enabled);
  const account = usable.find((a) => a.id === accountId) ?? usable[0] ?? null;
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [filling, setFilling] = useState<StayBookingDto | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  if (!account) {
    return (
      <EmptyState icon={<KeyRound className="size-6" />} title="Нет доступных аккаунтов">
        {can('stays.account.manage')
          ? 'Подключите аккаунт Airbnb или Booking во вкладке «Аккаунты».'
          : 'Попросите администратора выдать вам доступ к аккаунту Airbnb или Booking.'}
      </EmptyState>
    );
  }
  const platform = PLATFORM[account.platform];

  const paste = async () => {
    try {
      setText(await navigator.clipboard.readText());
    } catch {
      setError('Телефон не дал прочитать буфер обмена — вставьте текст в поле вручную (долгое нажатие → «Вставить»).');
    }
  };

  /**
   * Логин аккаунта — в буфер обмена: вставить в приложение или на сайт
   * площадки. Пароля на телефоне нет: его знает сотрудник или администратор,
   * а на компьютере BUCOR входит сам.
   */
  const copyLogin = async () => {
    setError(null);
    try {
      const { login } = await api.stayAccountLogin(account.id);
      await navigator.clipboard.writeText(login);
      setCopied(login);
    } catch (err) {
      setError(err instanceof Error && err.name === 'NotAllowedError' ? 'Телефон не дал записать в буфер обмена — попробуйте ещё раз.' : describeError(err));
    }
  };

  const recognize = async () => {
    setError(null);
    setOutcome(null);
    setBusy(true);
    try {
      const url = link.trim() || platform.trips;
      const res = await api.captureStayBooking({
        accountId: account.id,
        url,
        title: '',
        text,
        jsonLd: [],
        links: link.trim() ? [{ href: link.trim(), text: '' }] : [],
        headings: [],
        city: null,
        country: null,
        manual: true,
      });
      if (res.booking) {
        setOutcome({ kind: 'saved', booking: res.booking, existed: res.result !== 'created' });
        setText('');
        setLink('');
        if (res.booking.draft && res.booking.canEdit) setFilling(res.booking);
      } else {
        setOutcome({ kind: res.result === 'incomplete' ? 'incomplete' : 'notFound' });
      }
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* ── Аккаунт ── */}
      <div className="-mx-3 flex gap-1.5 overflow-x-auto px-3 pb-0.5">
        {usable.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onAccountChange(a.id)}
            className={cn(
              'inline-flex flex-none items-center gap-2 whitespace-nowrap rounded-full border py-1 pl-1 pr-3 text-[13px] font-medium',
              a.id === account.id ? 'border-transparent bg-heading text-card' : 'border-border bg-card text-soft',
            )}
          >
            <PlatformMark platform={a.platform} size="sm" />
            {a.title}
          </button>
        ))}
      </div>

      {/* ── Открыть площадку ── */}
      <section className="rounded-2xl border border-border bg-card p-3.5 shadow-sm">
        <p className="text-[13px] leading-snug text-soft">
          Бронируйте в приложении или на сайте {platform.label} под аккаунтом «{account.title}». Автовход есть только в BUCOR на
          компьютере: на телефоне сессия {platform.label} своя, и если площадка попросит войти — скопируйте логин аккаунта, пароль
          уточните у администратора.
        </p>
        <Button variant="outline" className="mt-3 h-11 w-full rounded-xl" onClick={() => void copyLogin()}>
          <Copy className="size-4" /> {copied ? 'Логин скопирован' : 'Скопировать логин'}
        </Button>
        {copied && <p className="mt-1.5 truncate text-center text-[12px] text-muted-foreground">{copied}</p>}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <ToolLink icon={<ExternalLink className="size-4" />} label={`Открыть ${platform.label}`} onClick={() => openExternal(platform.home)} />
          <ToolLink icon={<Luggage className="size-4" />} label="Брони на сайте" onClick={() => openExternal(platform.trips)} />
          <ToolLink icon={<MessageCircle className="size-4" />} label="Переписка" onClick={() => openExternal(platform.messages)} />
          <ToolLink icon={<LifeBuoy className="size-4" />} label="Поддержка" onClick={() => openExternal(platform.support)} />
        </div>
      </section>

      {/* ── Запись брони из текста ── */}
      {can('stays.book') && (
        <section className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-3.5 shadow-sm">
          <div>
            <p className="text-[15px] font-semibold text-heading">Записать бронь из текста</p>
            <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
              Откройте бронь в {platform.label} (подтверждение, детали поездки или письмо), выделите всё и скопируйте. Вставьте сюда — даты,
              сумма и код подставятся сами, останется указать менеджера и работника.
            </p>
          </div>
          <textarea
            className={cn(TEXTAREA, 'min-h-[140px]')}
            placeholder="Текст брони…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <FormField label="Ссылка на бронь или квартиру (необязательно)">
            <input className={INPUT} inputMode="url" placeholder={`${platform.home}…`} value={link} onChange={(e) => setLink(e.target.value)} />
          </FormField>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => void paste()}>
              <ClipboardPaste className="size-4" /> Вставить
            </Button>
            <Button className="flex-1 rounded-full" disabled={busy || text.trim().length < 20} onClick={() => void recognize()}>
              {busy ? 'Распознаю…' : 'Распознать и записать'}
            </Button>
          </div>

          {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-[13px] text-destructive">{error}</p>}
          {outcome?.kind === 'saved' && (
            <p className="rounded-xl bg-success/10 px-3 py-2 text-[13px] text-heading">
              {outcome.existed ? 'Эта бронь уже была записана — дополнена: ' : 'Бронь записана: '}
              <span className="font-semibold">{outcome.booking.title}</span> · {formatRange(outcome.booking.checkIn, outcome.booking.checkOut)}
              {outcome.booking.priceMinor !== null && <> · {formatMoney(outcome.booking.priceMinor, outcome.booking.currency)}</>}
            </p>
          )}
          {outcome && outcome.kind !== 'saved' && (
            <div className="flex flex-col gap-2 rounded-xl bg-warning/10 px-3 py-2 text-[13px] text-heading">
              {outcome.kind === 'incomplete'
                ? 'Код брони нашёлся, а даты — нет. Скопируйте текст целиком или запишите бронь вручную.'
                : 'В тексте не нашёлся код брони (Airbnb — HM…, Booking — номер подтверждения). Скопируйте страницу подтверждения целиком или запишите вручную.'}
              <Button size="sm" variant="outline" className="self-start rounded-full" onClick={() => onBook({ accountId: account.id })}>
                Записать вручную
              </Button>
            </div>
          )}
        </section>
      )}

      <BookingDialog
        open={filling !== null}
        booking={filling}
        accounts={accounts}
        people={people}
        onClose={() => setFilling(null)}
        onSaved={(b) => {
          setFilling(null);
          setOutcome({ kind: 'saved', booking: b, existed: true });
        }}
      />
    </div>
  );
}

function ToolLink({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-11 items-center justify-center gap-2 rounded-xl border border-border bg-secondary/50 px-2 text-[13px] font-medium text-heading"
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}
